import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Award, Edit, Trash2, Send, MessageSquare, Plus, BarChart2 } from 'lucide-react';
import TeamInvite from './TeamInvite';
import Navigation from '../common/Navigation';
import TeamChallengeModal from './TeamChallengeModal';

const TeamManagement = () => {
    const [teams, setTeams] = useState([
        {
            id: 1,
            name: 'Step Legends',
            members: [
                { id: 1, name: 'John Walker', role: 'leader', avatar: '/api/placeholder/40/40', weeklySteps: 87245 },
                { id: 2, name: 'Sarah Miles', role: 'member', avatar: '/api/placeholder/40/40', weeklySteps: 65432 },
                { id: 3, name: 'Mike Step', role: 'member', avatar: '/api/placeholder/40/40', weeklySteps: 54321 }
            ],
            activeChallenges: [
                { id: 3, title: 'Team Distance Challenge', progress: 65 }
            ],
            totalSteps: 206998,
            createdDate: 'Mar 10, 2025'
        }
    ]);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTeamName, setNewTeamName] = useState('');
    const [activeTeamId, setActiveTeamId] = useState(1);
    const [showChallengeModal, setShowChallengeModal] = useState(false);

    const activeTeam = teams.find(team => team.id === activeTeamId);

    const createTeam = () => {
        if (newTeamName.trim() === '') return;

        const newTeam = {
            id: teams.length + 1,
            name: newTeamName,
            members: [
                { id: 1, name: 'John Walker', role: 'leader', avatar: '/api/placeholder/40/40', weeklySteps: 87245 }
            ],
            activeChallenges: [],
            totalSteps: 87245,
            createdDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        };

        setTeams([...teams, newTeam]);
        setNewTeamName('');
        setShowCreateModal(false);
        setActiveTeamId(newTeam.id);
    };
    const createTeamChallenge = (challengeData) => {
        setTeams(teams.map(team => {
            if (team.id === activeTeamId) {
                return {
                    ...team,
                    activeChallenges: [...team.activeChallenges, challengeData]
                };
            }
            return team;
        }));
    };
    const removeTeamMember = (memberId) => {
        setTeams(teams.map(team => {
            if (team.id === activeTeamId) {
                const updatedMembers = team.members.filter(member => member.id !== memberId);
                const newTotalSteps = updatedMembers.reduce((sum, member) => sum + member.weeklySteps, 0);
                return {
                    ...team,
                    members: updatedMembers,
                    totalSteps: newTotalSteps
                };
            }
            return team;
        }));
    };

    return (
        <div className="min-h-screen bg-background-light">
            <nav className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white shadow-sm z-20 flex-col">
                <Navigation />
            </nav>
            <div className="max-w-7xl lg:ml-64 overflow-auto">
                <header className="mb-6 m-3">
                    <h1 className="text-2xl text-primary font-bold">Team Management</h1>
                    <p className="text-gray-500">Create and manage your walking teams</p>
                </header>

                <div className="flex flex-col lg:flex-row gap-6">
                    {/* Team List */}
                    <div className="lg:w-1/3">
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold">Your Teams</h2>
                                <button
                                    className="bg-primary text-white p-2 rounded-full"
                                    onClick={() => setShowCreateModal(true)}
                                >
                                    <UserPlus size={20} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {teams.map(team => (
                                    <div
                                        key={team.id}
                                        className={`p-4 rounded-lg cursor-pointer ${activeTeamId === team.id ? 'bg-primary bg-opacity-10 border border-primary' : 'bg-gray-50 hover:bg-gray-100'}`}
                                        onClick={() => setActiveTeamId(team.id)}
                                    >
                                        <div className="flex justify-between items-center">
                                            <h3 className="font-medium">{team.name}</h3>
                                            <span className="text-sm text-gray-500">{team.members.length} members</span>
                                        </div>
                                        <div className="flex mt-2">
                                            <div className="flex -space-x-2">
                                                {team.members.slice(0, 3).map(member => (
                                                    <img
                                                        key={member.id}
                                                        src={member.avatar}
                                                        alt={member.name}
                                                        className="w-8 h-8 rounded-full border-2 border-white"
                                                    />
                                                ))}
                                                {team.members.length > 3 && (
                                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs border-2 border-white">
                                                        +{team.members.length - 3}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Team Details */}
                    {activeTeam && (
                        <div className="lg:w-2/3">
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-primary bg-opacity-10 rounded-full mr-4">
                                            <Users size={24} className="text-primary" />
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-semibold">{activeTeam.name}</h2>
                                            <p className="text-gray-500">Created {activeTeam.createdDate}</p>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button className="p-2 rounded-full bg-gray-100">
                                            <Edit size={18} />
                                        </button>
                                        <button className="p-2 rounded-full bg-gray-100">
                                            <MessageSquare size={18} />
                                        </button>
                                    </div>
                                </div>

                                {/* Team Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <p className="text-gray-500">Total Steps</p>
                                        <p className="text-2xl font-bold">{activeTeam.totalSteps.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <p className="text-gray-500">Team Members</p>
                                        <p className="text-2xl font-bold">{activeTeam.members.length}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <p className="text-gray-500">Active Challenges</p>
                                        <p className="text-2xl font-bold">{activeTeam.activeChallenges.length}</p>
                                    </div>
                                </div>

                                {/* Team Members */}
                                <div className="mb-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold">Team Members</h3>
                                        <button
                                            className="text-primary flex items-center text-sm"
                                            onClick={() => setShowInviteModal(true)}
                                        >
                                            <UserPlus size={16} className="mr-1" />
                                            Invite Members
                                        </button>
                                    </div>

                                    <div className="space-y-3">
                                        {activeTeam.members.map(member => (
                                            <div key={member.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                                <div className="flex items-center">
                                                    <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full mr-3" />
                                                    <div>
                                                        <p className="font-medium">{member.name}</p>
                                                        <p className="text-sm text-gray-500 capitalize">{member.role}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="mr-4 text-right">
                                                        <p className="text-sm text-gray-500">Weekly Steps</p>
                                                        <p className="font-medium">{member.weeklySteps.toLocaleString()}</p>
                                                    </div>
                                                    {member.role !== 'leader' && (
                                                        <button
                                                            className="text-gray-400 hover:text-red-500"
                                                            onClick={() => removeTeamMember(member.id)}
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Team Challenges */}
                                <div>
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold">Team Challenges</h3>
                                        <div className="flex space-x-2">
                                            <button
                                                className="text-primary flex items-center text-sm"
                                                onClick={() => setShowChallengeModal(true)}
                                            >
                                                <Plus size={16} className="mr-1" />
                                                Create Challenge
                                            </button>
                                            <button className="text-primary flex items-center text-sm">
                                                <Award size={16} className="mr-1" />
                                                Browse Challenges
                                            </button>
                                        </div>
                                    </div>

                                    {activeTeam.activeChallenges.length > 0 ? (
                                        <div className="space-y-3">
                                            {activeTeam.activeChallenges.map(challenge => (
                                                <div key={challenge.id} className="bg-gray-50 rounded-lg p-4">
                                                    <div className="flex justify-between items-center">
                                                        <h4 className="font-medium">{challenge.title}</h4>
                                                        <div className="flex items-center">
                                                            <span className="text-sm mr-2">{challenge.progress}% complete</span>
                                                            <button className="p-1 text-gray-500 hover:text-primary">
                                                                <BarChart2 size={16} />
                                                            </button>
                                                            <button className="p-1 text-gray-500 hover:text-primary ml-1">
                                                                <Edit size={16} />
                                                            </button>
                                                            <button className="p-1 text-gray-500 hover:text-red-500 ml-1">
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2 h-2 w-full bg-gray-200 rounded-full">
                                                        <div
                                                            className="h-full rounded-full bg-primary"
                                                            style={{ width: `${challenge.progress}%` }}
                                                        />
                                                    </div>
                                                    {challenge.description && (
                                                        <p className="text-sm text-gray-500 mt-2">{challenge.description}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                                            <p className="text-gray-500">No active challenges</p>
                                            <div className="mt-2 flex justify-center space-x-2">
                                                <button className="text-primary" onClick={() => setShowChallengeModal(true)}>Create a challenge</button>
                                                <span className="text-gray-400">or</span>
                                                <button className="text-primary">Browse challenges</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="md:hidden block sticky bottom-0 z-50 bg-white border-t border-gray-200 p-2">
                <Navigation orientation='horizontal' />
            </div>
            {/* Create Team Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-semibold mb-4">Create New Team</h2>
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">Team Name</label>
                            <input
                                type="text"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                value={newTeamName}
                                onChange={(e) => setNewTeamName(e.target.value)}
                                placeholder="Enter team name"
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button
                                className="px-4 py-2 border border-gray-300 rounded-md"
                                onClick={() => setShowCreateModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-primary text-white rounded-md"
                                onClick={createTeam}
                            >
                                Create Team
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="w-full max-w-md">
                        <TeamInvite
                            teamId={activeTeamId}
                            teamName={activeTeam.name}
                            onClose={() => setShowInviteModal(false)}
                        />
                    </div>
                </div>
            )}

          
            {showChallengeModal && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="w-full max-w-md">
            <TeamChallengeModal 
                teamId={activeTeamId}
                teamName={activeTeam.name}
                onClose={() => setShowChallengeModal(false)}
                onCreateChallenge={createTeamChallenge}
            />
        </div>
    </div>
)}
        </div>
    );
};

export default TeamManagement;